import SingleDate from '@/components/date/SingleDate';
import { AccountViewModel } from '@/models/AccountViewModel';
import { RecordResponse } from '@/models/ServiceTypes';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import CategorySelector from '@/modules/cashFlow/components/CategorySelector';
import {
  ImportValidationError,
  resolveImportError,
} from '@/modules/cashFlow/importErrorRouting';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import {
  UnfinishedImport,
  useAddTransactionMutation,
  useAssociateUploadAccountMutation,
  useGetUnfinishedImportsQuery,
  useUploadTransactionsMutation,
} from '@/store/transactionsApi';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Close from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import dayjs from '@/config/dayjs';
import { saveAs } from 'file-saver';
import React, { useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { APP_DATE_FORMAT } from '@/utils/date';
import { formatNumberToCurrency } from '@/utils/currency';
import type { CurrencyInputOnChangeValues } from 'react-currency-input-field';
import AdornedCurrencyInput from '@/components/common/AdornedCurrencyInput';

interface AddTransactionModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: number;
  investorId?: number;
}

interface AddTransactionSaveFeedback {
  message: string;
  severity: 'success' | 'warning' | 'error';
}

interface ModalErrorObject {
  data?: {
    message?: string;
  };
  error?: string;
  message?: string;
}

type ModalError = ModalErrorObject | Error | string | null | undefined;

const CONFIRM_LOCATION_WARNING_KEY =
  'addTransactionModal.confirmLocationWarning';

const getMutationErrorMessage = (err: ModalError, fallback: string): string => {
  if (typeof err === 'string') {
    return err;
  }

  if (typeof err !== 'object' || err === null) {
    return fallback;
  }

  if ('data' in err && typeof err.data?.message === 'string') {
    return err.data.message;
  }

  if ('error' in err && typeof err.error === 'string') {
    return err.error;
  }

  if (typeof err.message === 'string') {
    return err.message;
  }

  return fallback;
};

const AddTransactionModal: React.FC<AddTransactionModalProps> = ({
  open,
  onClose,
  initialTab = 0,
  investorId,
}) => {
  const { t } = useLocalization();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [account, setAccount] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [amountValue, setAmountValue] = useState<number | null>(null);
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [headerValidationError, setHeaderValidationError] = useState<
    string | null
  >(null);
  const [selectedAccountIdForUpload, setSelectedAccountIdForUpload] =
    useState<string>('');
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  const [associationError, setAssociationError] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'newUpload' | 'finishImport'>(
    'newUpload',
  );
  const [selectedUnfinishedImportId, setSelectedUnfinishedImportId] = useState<
    number | null
  >(null);
  const [saveFeedback, setSaveFeedback] =
    useState<AddTransactionSaveFeedback | null>(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [
    addTransaction,
    { isLoading: isSaving, error: saveError, reset: resetAddTransaction },
  ] = useAddTransactionMutation();

  // Hold the latest `reset` in a ref so the on-open effect can call it WITHOUT
  // depending on it. RTK may hand back a new `reset` when the mutation result
  // updates (e.g. as a save settles); if that were an effect dependency, the
  // on-open reset would re-run mid-save and wipe the user's input (Codex review).
  const resetAddTransactionRef = React.useRef(resetAddTransaction);
  // Latest `isSaving` in a ref for the on-open effect: it must read the current
  // value without listing it as a dependency, or the effect would re-run (and
  // re-reset everything) every time a save toggles isSaving mid-session.
  const isSavingRef = React.useRef(isSaving);
  // Latest `isProcessingUpload` in a ref so the async handleSave success path
  // reads the CURRENT value, not the one captured when Save was clicked: a user
  // can start a bulk import while a manual save is still in flight, and the
  // stale closure value would let the save's onClose() close over that import.
  const isProcessingUploadRef = React.useRef(isProcessingUpload);
  React.useEffect(() => {
    resetAddTransactionRef.current = resetAddTransaction;
    isSavingRef.current = isSaving;
    isProcessingUploadRef.current = isProcessingUpload;
  }, [resetAddTransaction, isSaving, isProcessingUpload]);

  const [uploadTransactions] = useUploadTransactionsMutation();

  const [associateUploadAccount] = useAssociateUploadAccountMutation();

  const { data: unfinishedImportsData, isLoading: isLoadingUnfinishedImports } =
    useGetUnfinishedImportsQuery(undefined, {
      skip: !open || activeTab !== 1,
      pollingInterval: 30000,
    });

  const { isLoading: isLoadingCategories, error: categoriesError } =
    useGetCashflowCategoriesQuery({ InvestorId: investorId });

  const {
    data: accountsDataRaw,
    isLoading: isLoadingAccountsRaw,
    error: accountsErrorRaw,
  } = useGetAccountsQuery({
    showClosed: false,
    ...skipViewingInvestorTagForAccountsQuery,
  });

  // The RTK `useQuery` return type collapses to `any` under the type-aware lint
  // service, so its fields read as unsafe even though tsc types them correctly.
  // Re-assert the consumed fields to their real shapes so member access is safe.
  const accountsResponse = accountsDataRaw as AccountViewModel | undefined;
  const isLoadingAccounts = isLoadingAccountsRaw as boolean;
  const accountsError = accountsErrorRaw as
    | FetchBaseQueryError
    | SerializedError
    | undefined;
  const unfinishedImports: RecordResponse<UnfinishedImport> | undefined =
    unfinishedImportsData;

  // `Records` is typed non-null, but defend against an unexpected/empty API body
  // so the render paths below can't crash on `.map`/`.length`. The `.find` logic
  // paths keep trusting the type since they run inside the try/catch (EUD-414).
  const accountRecords = accountsResponse?.Records ?? [];
  const importRecords = unfinishedImports?.Records ?? [];

  const handleSave = async () => {
    resetAddTransaction();
    setSaveFeedback(null);
    if (!description || !category || !account || amount === '' || !date) {
      console.error('Please fill all required fields.');
      return;
    }

    const parsedAmount = amountValue;
    if (parsedAmount === null || Number.isNaN(parsedAmount)) {
      console.error('Invalid amount value.');
      return;
    }

    const transactionData = {
      DisplayDescription: description,
      BankTransactionCategoryId: parseInt(category),
      WealthAccountId: parseInt(account),
      Amount: parsedAmount,
      // Legacy API expects both human-readable Date and ISO TransactionDate
      Date: dayjs(date).format('M/D/YYYY'),
      TransactionDate: `${date}T00:00:00.000Z`,
      ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
    };

    try {
      const result = await addTransaction(transactionData).unwrap();
      if (result.IsVisibleInCashflow === false) {
        const ruleText = result.AppliedRuleText
          ? t(
              'addTransactionModal.savedButHiddenRuleText',
              ' matching "{{rule}}"',
              { rule: result.AppliedRuleText },
            )
          : '';
        const rawResolvedType =
          result.ResolvedTransactionType === 'Brokerage'
            ? 'Investment Activity'
            : result.ResolvedTransactionType || 'Investment Activity';
        const resolvedType = t(rawResolvedType);
        setSaveFeedback({
          severity: 'warning',
          message: t(
            'addTransactionModal.savedButHidden',
            'Transaction was saved, but a rule{{ruleText}} moved it to {{resolvedType}}, so it will not appear in Cashflow.',
            { ruleText, resolvedType },
          ),
        });
        setDescription('');
        setCategory('');
        setAccount('');
        setAmount('');
        setAmountValue(null);
        setDate(dayjs().format('YYYY-MM-DD'));
        return;
      }

      setDescription('');
      setCategory('');
      setAccount('');
      setAmount('');
      setAmountValue(null);
      setDate(dayjs().format('YYYY-MM-DD'));
      setSnackbarOpen(true);
      // A manual save can complete while a bulk import is still in flight (tabs
      // aren't disabled during an upload), so don't force-close over it — the
      // success snackbar renders outside the Dialog and still confirms the add.
      // Read via ref, not the closure's captured value: the import can start
      // AFTER Save was clicked, so the captured `isProcessingUpload` is stale.
      // Not routed through handleRequestClose: `isSaving` is still true in this
      // closure, so that guard would block the very close the save just earned.
      if (!isProcessingUploadRef.current) {
        onClose();
      }
    } catch (err) {
      console.error('Failed to add transaction:', err);
      const mutationError = err as ModalError;
      const rawErrorMessage = getMutationErrorMessage(
        mutationError,
        t('addTransactionModal.errorSaving'),
      );
      const errorMessage = t(rawErrorMessage, rawErrorMessage);
      if (rawErrorMessage === CONFIRM_LOCATION_WARNING_KEY) {
        setSaveFeedback({
          severity: 'warning',
          message: errorMessage,
        });
        setDescription('');
        setCategory('');
        setAccount('');
        setAmount('');
        setAmountValue(null);
        setDate(dayjs().format('YYYY-MM-DD'));
        return;
      }

      setSaveFeedback({
        severity: 'error',
        message: errorMessage,
      });
    }
  };

  const handleAmountChange = (
    value: string,
    values?: CurrencyInputOnChangeValues,
  ) => {
    setAmount(value);
    setAmountValue(values?.float ?? null);
  };

  const handleDateChange = (newDate: string) => {
    if (newDate) {
      const [month, day, year] = newDate.split('/');
      setDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
  };

  const handleSnackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleProcessImport = async () => {
    // Reset errors/success messages before starting
    setUploadError(null);
    setAssociationError(null);
    setUploadSuccess(null);
    setHeaderValidationError(null);
    setIsProcessingUpload(true);

    // Track which step failed so the catch can pick the right message without
    // string-matching error text (the old `UploadId` heuristic — EUD-414).
    let step: 'upload' | 'associate' = 'upload';

    try {
      // --- COMMON PRE-CHECK: Account must be selected ---
      if (!selectedAccountIdForUpload) {
        throw new ImportValidationError(
          t('addTransactionModal.pleaseSelectAccount'),
        );
      }

      if (importMode === 'newUpload') {
        // --- Mode: New Upload ---
        if (!selectedFile) {
          // Header validation also prevents upload if file invalidates selection
          throw new ImportValidationError(
            t('addTransactionModal.pleaseSelectValidCsv'),
          );
        }
        // Additional check for header validity state, though button disabled should cover it
        if (headerValidationError) {
          throw new ImportValidationError(
            t('addTransactionModal.csvHeaderValidationError', {
              error: headerValidationError,
            }),
          );
        }

        console.log('[New Upload] Step 1: Uploading file...');
        const uploadResult = (await uploadTransactions({
          file: selectedFile,
        }).unwrap()) as
          | Awaited<ReturnType<ReturnType<typeof uploadTransactions>['unwrap']>>
          | null
          | undefined;

        if (!uploadResult || !uploadResult.UploadId) {
          // Handle case where upload might succeed but response is unexpected
          throw new ImportValidationError(
            t('addTransactionModal.uploadFailedNoId'),
          );
        }

        // Upload succeeded; any failure from here on is the association step.
        step = 'associate';

        console.log('[New Upload] Step 2: Association payload construction...');
        // Construct payload using data from upload response
        const associationPayload = {
          UploadId: uploadResult.UploadId,
          NumberOfTemporaryTransactions:
            uploadResult.NumberOfTemporaryTransactions,
          // Map each account from the upload response to the selected target
          // account. AccountName must echo the name the upload created the
          // temporary transactions under (the CSV's account name, returned in
          // the upload response) so the backend can match them; WealthAccountId
          // points them at the account the user chose to import into.
          Accounts: uploadResult.Accounts.map((acc) => ({
            AccountName: acc.AccountName,
            WealthAccountId: parseInt(selectedAccountIdForUpload), // Target account ID
          })),
          Categories: [], // Assuming no category mapping in this step
        };

        console.log('[New Upload] Step 3: Associating transactions...');
        const associationResult =
          await associateUploadAccount(associationPayload).unwrap();
        console.log('[New Upload] Association successful:', associationResult);

        setUploadSuccess(
          t('addTransactionModal.importComplete', {
            count: associationResult.NumberOfTransactionsAdded,
          }),
        );
        // Reset relevant form state on success
        setSelectedFile(null);
        setSelectedAccountIdForUpload('');
        // Keep mode as newUpload
      } else {
        // Implicitly importMode === "finishImport"
        // --- Mode: Finish Existing Import ---
        if (!selectedUnfinishedImportId) {
          throw new ImportValidationError(
            t('addTransactionModal.pleaseSelectUnfinished'),
          );
        }

        const selectedImport = unfinishedImports?.Records.find(
          (imp) => imp.UploadId === selectedUnfinishedImportId,
        );
        const targetAccount = accountsResponse?.Records.find(
          (acc) => acc.AccountId === parseInt(selectedAccountIdForUpload),
        );

        if (!selectedImport || !targetAccount) {
          // This case should ideally be prevented by UI logic/disabled states
          throw new ImportValidationError(
            t('addTransactionModal.couldNotFindImport'),
          );
        }

        console.log(
          '[Finish Import] Step 1: Association payload construction...',
        );
        // Construct payload - **Using target account name as placeholder AccountName**
        // This mapping might need refinement based on exact API behavior for matching
        const associationPayload = {
          UploadId: selectedImport.UploadId,
          NumberOfTemporaryTransactions:
            selectedImport.NumberOfTemporaryTransactions,
          Accounts: [
            {
              // NOTE: API needs to handle matching based on UploadId primarily.
              // Sending the target account name here for the required payload structure.
              AccountName:
                'Brokerage                                                                                         ', //targetAccount.Name,
              WealthAccountId: targetAccount.AccountId,
            },
          ],
          Categories: [], // Assuming no category mapping
        };

        console.log('[Finish Import] Step 2: Associating transactions...');
        const associationResult =
          await associateUploadAccount(associationPayload).unwrap();
        console.log(
          '[Finish Import] Association successful:',
          associationResult,
        );

        setUploadSuccess(
          t('addTransactionModal.importFinished', {
            count: associationResult.NumberOfTransactionsAdded,
          }),
        );
        // Reset relevant form state on success
        setSelectedUnfinishedImportId(null);
        setSelectedAccountIdForUpload('');
        setImportMode('newUpload'); // Switch back to new upload mode automatically
      }
    } catch (err) {
      // Log the raw error for support/debugging; never surface technical details
      // (API strings, internal ids) to the user (EUD-414).
      console.error(
        `Failed during the import process (mode: ${importMode}):`,
        err,
      );

      // Only our tagged validation errors carry friendly, actionable messages —
      // show those as-is. Everything else (API rejections, native runtime errors
      // like an unexpected response shape) falls back to a friendly message by
      // step, so raw technical text is never surfaced (only logged above).
      const { fallbackKey, isAssociationError } = resolveImportError(
        importMode,
        step,
      );
      const message =
        err instanceof ImportValidationError ? err.message : t(fallbackKey);

      if (isAssociationError) {
        setAssociationError(message);
      } else {
        setUploadError(message);
      }
    } finally {
      // --- Cleanup ---
      setIsProcessingUpload(false); // Ensure processing state is reset
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setUploadError(null);
    setUploadSuccess(null);
    resetAddTransaction();
    setSaveFeedback(null);
    setSelectedFile(null);
    setAssociationError(null);
    setHeaderValidationError(null);
  };

  // Block the close paths (backdrop/Esc/X/Cancel) while any write is in flight.
  // Bulk import: reopening onto a frozen modal pops the settled import's
  // success/error onto the fresh form. Manual save (isSaving): closing mid-POST
  // and reopening would let the on-open reset re-enable Save (duplicate submit)
  // and let the still-live handleSave closure wipe the fresh form (EUD-414).
  const handleRequestClose = () => {
    if (isProcessingUpload || isSaving) {
      return;
    }
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
      setUploadSuccess(null);
      setHeaderValidationError(null);
      setAssociationError(null);

      const requiredHeaders = [
        'Date',
        'Description',
        'Original Description',
        'Amount',
        'Transaction Type',
        'Category',
        'Account Name',
        'Labels',
        'Notes',
      ];

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (text) {
          const firstLine = text.split(/\r\n|\n/)[0];
          const headers = firstLine.split(',').map((header) => header.trim());

          const missingHeaders = requiredHeaders.filter(
            (reqHeader) =>
              !headers.some((h) => h.toLowerCase() === reqHeader.toLowerCase()),
          );

          if (missingHeaders.length > 0) {
            setHeaderValidationError(
              t('addTransactionModal.missingHeaders', {
                headers: missingHeaders.join(', '),
              }),
            );
            setSelectedFile(null);
          } else {
            setHeaderValidationError(null);
          }
        } else {
          setHeaderValidationError(
            t('addTransactionModal.couldNotReadFileHeader'),
          );
          setSelectedFile(null);
        }
      };
      reader.onerror = () => {
        setHeaderValidationError(t('addTransactionModal.errorReadingFile'));
        setSelectedFile(null);
      };

      reader.readAsText(file.slice(0, 1024));
    } else {
      setSelectedFile(null);
      setHeaderValidationError(null);
    }
  };

  // Function to handle CSV template download
  const handleDownloadTemplate = () => {
    const csvHeaders =
      'Date,Description,Original Description,Amount,Transaction Type,Category,Account Name,Labels,Notes';
    const blob = new Blob([csvHeaders], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'Transactions_Template.csv');
  };

  React.useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setUploadError(null);
      setUploadSuccess(null);
      setSelectedFile(null);
      setDescription('');
      setCategory('');
      setAccount('');
      setAmount('');
      setAmountValue(null);
      setDate(dayjs().format('YYYY-MM-DD'));
      setSaveFeedback(null);
      // The modal now stays mounted across background refetches (EUD-414), so
      // bulk-upload state no longer resets on unmount — clear it here or a stale
      // error/selection from a previous session lingers on a freshly opened modal.
      setAssociationError(null);
      setHeaderValidationError(null);
      setSelectedAccountIdForUpload('');
      setSelectedUnfinishedImportId(null);
      setImportMode('newUpload');
      // The manual-entry save error lives in RTK Query mutation state, not local
      // useState, so it also survives while the modal stays mounted — reset the
      // mutation so a prior "Error saving transaction" banner doesn't reappear.
      // Called via ref so `reset` isn't a dependency (see note above). Guarded on
      // !isSaving: reset() flips isSaving false without aborting the POST, which
      // would re-enable Save (duplicate submit) if a reopen ever raced an
      // in-flight save. handleRequestClose now blocks that reopen, so this is
      // belt-and-suspenders for a parent-forced reopen (EUD-414).
      if (!isSavingRef.current) {
        resetAddTransactionRef.current();
      }
    }
  }, [open, initialTab]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleRequestClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth='sm'
        aria-labelledby='add-transaction-modal-title'
        aria-describedby='add-transaction-modal-description'
        sx={{
          paddingTop:
            'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
          paddingBottom:
            'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
        }}
      >
        <DialogTitle>
          <Stack
            direction='row'
            justifyContent='space-between'
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant='h6'>
                {t('addTransactionModal.title')}
              </Typography>
            </Box>

            <Box>
              <IconButton
                aria-label={t('close')}
                onClick={handleRequestClose}
                disabled={isProcessingUpload || isSaving}
              >
                <Close />
              </IconButton>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            sx={{ mb: 2 }}
          >
            <Tab label={t('addTransactionModal.manualEntry')} />
            <Tab label={t('addTransactionModal.bulkUpload')} />
          </Tabs>

          {activeTab === 0 && (
            <Stack spacing={3}>
              {saveFeedback && (
                <Alert
                  severity={saveFeedback.severity}
                  onClose={() => {
                    resetAddTransaction();
                    setSaveFeedback(null);
                  }}
                >
                  {saveFeedback.message}
                </Alert>
              )}

              <TextField
                required
                id='description'
                label={t('addTransactionModal.descriptionLabel')}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                fullWidth
              />

              <CategorySelector
                label={t('addTransactionModal.categoryLabel')}
                value={category}
                onChange={(id) => {
                  setCategory(String(id));
                }}
                required
                disabled={isLoadingCategories || !!categoriesError}
                investorId={investorId}
              />

              <FormControl
                required
                fullWidth
              >
                <InputLabel id='account-select-label'>
                  {t('addTransactionModal.accountLabel')}
                </InputLabel>
                <Select
                  labelId='account-select-label'
                  id='account'
                  value={account}
                  label={t('addTransactionModal.accountLabel')}
                  onChange={(e) => {
                    setAccount(e.target.value);
                  }}
                  disabled={isLoadingAccounts || !!accountsError}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: { xs: 'calc(100vw - 32px)', sm: 540 },
                      },
                    },
                  }}
                >
                  <MenuItem
                    value=''
                    sx={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      alignItems: 'flex-start',
                    }}
                  >
                    {isLoadingAccounts
                      ? t('addTransactionModal.loadingAccounts')
                      : t('addTransactionModal.selectAccount')}
                  </MenuItem>
                  {accountRecords.map((acc) => (
                    <MenuItem
                      key={acc.AccountId}
                      value={acc.AccountId}
                      sx={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        alignItems: 'flex-start',
                        display: 'block',
                      }}
                    >
                      <Box
                        component='span'
                        sx={{ display: 'inline' }}
                      >
                        {acc.Name} ({acc.InstitutionName})
                      </Box>{' '}
                      <Box
                        component='span'
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        - {t('addTransactionModal.balance')}:{' '}
                        {formatNumberToCurrency(acc.Balance)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {accountsError && (
                  <FormHelperText error>
                    {t('addTransactionModal.errorLoadingAccounts')}
                  </FormHelperText>
                )}
              </FormControl>

              <AdornedCurrencyInput
                required
                id='amount'
                label={t('addTransactionModal.amountLabel')}
                value={amount}
                allowNegativeValue
                decimalsLimit={2}
                onValueChange={(value, _name, values) => {
                  handleAmountChange(value || '', values);
                }}
                fullWidth
              />

              <Box sx={{ width: '100%' }}>
                <SingleDate
                  date={date ? dayjs(date).format(APP_DATE_FORMAT) : ''}
                  onChange={handleDateChange}
                  label={t('addTransactionModal.dateLabel')}
                  showShortcuts={false}
                />
              </Box>

              {saveError && !saveFeedback && (
                <Typography
                  color='error'
                  variant='body2'
                  sx={{ mt: 1 }}
                >
                  {t('addTransactionModal.errorSaving')}
                </Typography>
              )}
            </Stack>
          )}

          {activeTab === 1 && (
            <Stack
              spacing={2}
              alignItems='center'
              sx={{ width: '100%' }}
            >
              <Typography variant='body1'>
                {t('addTransactionModal.importDescription1')}
              </Typography>

              <Typography variant='body1'>
                {t('addTransactionModal.importDescription2')}
                <Link
                  component='button'
                  variant='body1'
                  onClick={handleDownloadTemplate}
                >
                  {t('addTransactionModal.downloadableTemplate')}
                </Link>
                .
              </Typography>

              <Box sx={{ textAlign: 'left', width: '100%', mb: 3 }}>
                <Typography
                  component='div'
                  variant='body1'
                >
                  <ul style={{ paddingLeft: '20px', margin: '8px 0' }}>
                    <li>{t('addTransactionModal.importInstructions.item1')}</li>
                    <li>{t('addTransactionModal.importInstructions.item2')}</li>
                    <li>{t('addTransactionModal.importInstructions.item3')}</li>
                  </ul>
                </Typography>
              </Box>

              <FormControl
                component='fieldset'
                sx={{ mb: 3 }}
              >
                <RadioGroup
                  row
                  aria-label='import-mode'
                  name='import-mode-radio-buttons-group'
                  value={importMode}
                  onChange={(e) => {
                    setImportMode(
                      e.target.value as 'newUpload' | 'finishImport',
                    );
                    setSelectedFile(null);
                    setHeaderValidationError(null);
                    setSelectedUnfinishedImportId(null);
                    setUploadError(null);
                    setAssociationError(null);
                    setUploadSuccess(null);
                  }}
                >
                  <FormControlLabel
                    value='newUpload'
                    control={<Radio />}
                    label={t('addTransactionModal.uploadNewFile')}
                    disabled={isProcessingUpload}
                  />
                  <FormControlLabel
                    value='finishImport'
                    control={<Radio />}
                    label={t('addTransactionModal.finishExistingImport')}
                    disabled={
                      isProcessingUpload ||
                      !unfinishedImports ||
                      importRecords.length === 0
                    }
                  />
                </RadioGroup>
              </FormControl>

              {importMode === 'finishImport' && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Typography
                    variant='subtitle2'
                    gutterBottom
                  >
                    {t('addTransactionModal.selectUnfinishedImport')}
                  </Typography>
                  {isLoadingUnfinishedImports && <CircularProgress size={20} />}
                  {importRecords.map((imp: UnfinishedImport) => (
                    <Paper
                      key={imp.UploadId}
                      variant='outlined'
                      onClick={() => {
                        if (!isProcessingUpload) {
                          setSelectedUnfinishedImportId(imp.UploadId);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: isProcessingUpload ? 'default' : 'pointer',
                        border:
                          selectedUnfinishedImportId === imp.UploadId ? 2 : 1,
                        borderColor:
                          selectedUnfinishedImportId === imp.UploadId
                            ? 'primary.main'
                            : 'divider',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 'bold' }}
                      >
                        {t('addTransactionModal.fileImportedOn')}{' '}
                        {dayjs(imp.ImportDate).format(APP_DATE_FORMAT)}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ mt: 0.5 }}
                      >
                        {imp.NumberOfTemporaryTransactions}{' '}
                        {t('addTransactionModal.transactionsPending')}
                      </Typography>
                    </Paper>
                  ))}
                  {importRecords.length === 0 && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {t('addTransactionModal.noUnfinishedImports')}
                    </Typography>
                  )}
                </Box>
              )}

              {importMode === 'newUpload' && (
                <Stack
                  spacing={2}
                  sx={{ width: '100%', mb: 4 }}
                >
                  <Button
                    variant='outlined'
                    component='label'
                    startIcon={<UploadFileIcon />}
                    disabled={isProcessingUpload}
                  >
                    {t('addTransactionModal.selectCsvFile')}
                    <input
                      type='file'
                      hidden
                      accept='.csv'
                      onChange={handleFileChange}
                      disabled={isProcessingUpload}
                    />
                  </Button>
                  {selectedFile && (
                    <Typography variant='body2'>{selectedFile.name}</Typography>
                  )}
                  {headerValidationError && (
                    <Alert
                      severity='error'
                      sx={{ mt: 1 }}
                    >
                      {headerValidationError}
                    </Alert>
                  )}
                </Stack>
              )}

              <FormControl
                required
                fullWidth
                sx={{ mb: 4 }}
              >
                <InputLabel id='upload-account-select-label'>
                  {t('addTransactionModal.selectAccountToImport')}
                </InputLabel>
                <Select
                  labelId='upload-account-select-label'
                  id='upload-account'
                  value={selectedAccountIdForUpload}
                  label={t('addTransactionModal.selectAccountToImport')}
                  onChange={(e) => {
                    setSelectedAccountIdForUpload(e.target.value);
                  }}
                  disabled={
                    isLoadingAccounts || isProcessingUpload || !!accountsError
                  }
                >
                  <MenuItem value=''>
                    {isLoadingAccounts
                      ? t('addTransactionModal.loadingAccounts')
                      : t('addTransactionModal.selectAccount')}
                  </MenuItem>
                  {accountRecords.map((acc) => (
                    <MenuItem
                      key={acc.AccountId}
                      value={acc.AccountId}
                      sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {acc.Name} ({acc.InstitutionName}) -{' '}
                      {t('addTransactionModal.balance')}:{' '}
                      {formatNumberToCurrency(acc.Balance)}
                    </MenuItem>
                  ))}
                </Select>
                {accountsError && (
                  <FormHelperText error>
                    {t('addTransactionModal.errorLoadingAccounts')}
                  </FormHelperText>
                )}
              </FormControl>

              {uploadError && <Alert severity='error'>{uploadError}</Alert>}

              {associationError && (
                <Alert severity='error'>{associationError}</Alert>
              )}

              {uploadSuccess && (
                <Alert severity='success'>{uploadSuccess}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {activeTab === 0 && (
            <>
              <Button
                variant='outlined'
                onClick={handleRequestClose}
              >
                {t('cancel')}
              </Button>
              <Button
                variant='contained'
                onClick={() => {
                  void handleSave();
                }}
                disabled={isSaving}
              >
                {isSaving ? t('saving', 'Saving...') : t('save', 'Save')}
              </Button>
            </>
          )}

          {activeTab === 1 && (
            <>
              <Button
                variant='outlined'
                onClick={handleRequestClose}
                disabled={isProcessingUpload}
              >
                {t('cancel')}
              </Button>

              <Button
                variant='contained'
                onClick={() => {
                  void handleProcessImport();
                }}
                disabled={
                  isProcessingUpload ||
                  !selectedAccountIdForUpload ||
                  (importMode === 'newUpload' &&
                    (!selectedFile || !!headerValidationError)) ||
                  (importMode === 'finishImport' && !selectedUnfinishedImportId)
                }
              >
                {importMode === 'newUpload'
                  ? t('addTransactionModal.uploadAndImport')
                  : t('addTransactionModal.finishImport')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity='success'
          sx={{ width: '100%' }}
        >
          {t('addTransactionModal.successMessage')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTransactionModal;
